import React, { useState, useEffect } from 'react';
import { TrashIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { RectangleStackIcon } from '@heroicons/react/24/solid';
import Alert from '../../global/Alert/Alert.tsx';
import { encodeData, encodeFiltersToParams } from '../../utils/encodeUtils.ts';
import SharedButton from '../../global/Button/Button.tsx';
import { userAppContext } from '../../Context/Context';
import { getFilters, deleteFilter } from '../../services/savedFiltersService';
import { updateFilter } from '../../services/savedFiltersService';

const ProfileFavorites = ({ favoritesClass }) => {
  const [savedSearches, setSavedSearches] = useState([]);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [showUpdateAlert, setShowUpdateAlert] = useState(false);
  const [activeTab, setActiveTab] = useState('filters');

  const { isAuthenticated } = userAppContext();

  const operatorMap = {
    eq: '=',
    neq: '≠',
    gt: '>',
    gte: '≥',
    lt: '<',
    lte: '≤',
    in: '∈',
    nin: '∉',
    contains: 'contains',
    startswith: 'starts with',
    endswith: 'ends with',
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadSavedSearches();
    }
  }, [isAuthenticated]);

  const loadSavedSearches = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getFilters();
      const cloudFilters = response.filters || [];

      const searches = cloudFilters.map(filter => {
        let type = filter.type || 'filter';
        
        if (!filter.type) {
          const hasColumns = filter.columnVisibilityModel || filter.columnOrder;
          const hasFilters = filter.filters && filter.filters.length > 0;
          const hasSearchQuery = filter.searchQuery && filter.searchQuery.trim() !== '';
          
          if (hasColumns && hasFilters) {
            type = 'complete';
          } else if (hasColumns) {
            type = 'columns';
          } else if (hasFilters) {
            type = 'filter';
          } else if (hasSearchQuery) {
            type = 'search';
          }
        }

        return {
          id: filter.filterId,
          table: filter.isEvidence ? 'Evidence' : 'Summary',
          filters: filter.filters,
          searchQuery: filter.searchQuery,
          savedAt: filter.createdAt,
          name: filter.name,
          type,
          columnOrder: filter.columnOrder,
          columnVisibilityModel: filter.columnVisibilityModel,
          searchContext: filter.searchContext,
          isLocal: false,
        };
      });

      const localColumnSelections = loadLocalColumnSelections();

      setSavedSearches([...searches, ...localColumnSelections]);
    } catch (err) {
      console.error('Error loading saved searches:', err);
      setError('Failed to load saved searches');
    } finally {
      setIsLoading(false);
    }
  };

  const loadLocalColumnSelections = () => {
    const columnSelections = [];
    const keys = Object.keys(localStorage);

    keys.forEach(key => {
      if (key.startsWith('columnSelection_') || key.startsWith('completeSearch_')) {
        try {
          const selection = JSON.parse(localStorage.getItem(key) || '');
          columnSelections.push({
            id: key,
            table: selection.searchContext?.isEvidence ? 'Evidence' : 'Summary',
            filters: selection.columnFilters || selection.columnVisibilityModel || [],
            searchQuery: selection.searchQuery || null,
            savedAt: selection.savedAt,
            name: selection.name,
            type: key.startsWith('completeSearch_') ? 'complete' : 'columns',
            columnOrder: selection.columnOrder,
            columnVisibilityModel: selection.columnVisibilityModel,
            searchContext: selection.searchContext,
            isLocal: true,
          });
        } catch (error) {
          console.error('Error parsing saved selection:', error);
        }
      }
    });

    return columnSelections.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());
  };

  const getFilteredSearches = () => {
    return savedSearches.filter(search => {
      if (activeTab === 'filters') {
        if (search.type === 'filter') return true;
        
        if (!search.type) {
          if (!Array.isArray(search.filters)) return false;
          const contextParams = ['view', 'tab', 'source', 'idents'];
          const columnFilters = search.filters.filter(f => !contextParams.includes(f.name));
          return columnFilters.length > 0 && !search.searchQuery;
        }
        
        return false;
      } else if (activeTab === 'searches') {
        return search.type === 'search' || search.type === 'complete';
      } else if (activeTab === 'columns') {
        return search.type === 'columns';
      }
      return false;
    });
  };

  const filteredSearches = getFilteredSearches();

  const buildSearchUrl = search => {
    try {
      const url = new URL(`${window.location.origin}/search`);

      if (search.searchQuery) {
        const searchParams = new URLSearchParams(search.searchQuery);
        searchParams.forEach((value, key) => {
          url.searchParams.set(key, value);
        });

        const filters = search.filters || [];
        if (filters.length > 0) {
          const contextParams = ['view', 'tab', 'source', 'idents'];
          const columnFilters = filters.filter(
            f =>
              !contextParams.includes(f.name) &&
              f.value !== undefined &&
              f.value !== null &&
              f.value !== '' &&
              !(Array.isArray(f.value) && f.value.length === 0),
          );

          if (columnFilters.length > 0) {
            const encodedFilters = encodeData(columnFilters);
            url.searchParams.set('f', encodedFilters);
          }
        }

        return url.href;
      }

      const filters = search.filters || [];
      if (filters.length === 0) return null;

      const viewFilter = filters.find(f => f.name === 'view');
      const tabFilter = filters.find(f => f.name === 'tab');
      const sourceFilter = filters.find(f => f.name === 'source');
      const identsFilter = filters.find(f => f.name === 'idents');

      url.searchParams.set('view', viewFilter?.value || 'ALL');
      url.searchParams.set('tab', tabFilter?.value || 'GDA');
      url.searchParams.set('source', sourceFilter?.value || 'ALL');
      url.searchParams.set('idents', identsFilter?.value || 'ALL');

      const columnFilters = filters.filter(
        f =>
          !['view', 'tab', 'source', 'idents'].includes(f.name) &&
          f.value !== undefined &&
          f.value !== null &&
          f.value !== '' &&
          !(Array.isArray(f.value) && f.value.length === 0),
      );

      if (columnFilters.length > 0) {
        const filterParams = encodeFiltersToParams(columnFilters);
        for (const [key, value] of filterParams.entries()) {
          url.searchParams.set(key, value);
        }
      }

      return url.href;
    } catch (error) {
      console.error('Error building search URL:', error);
      return null;
    }
  };

  const handleNavigateToSearch = search => {
    if (search.type === 'complete' && (search.columnVisibilityModel || search.columnOrder)) {
      const dataToStore = {
        columnVisibilityModel: search.columnVisibilityModel,
        columnOrder: search.columnOrder,
        name: search.name,
      };
      
      const tempKey = `_temp_loadComplete_${search.id}`;
      localStorage.setItem(tempKey, JSON.stringify(dataToStore));
    }

    if (search.searchQuery) {
      const targetUrl = buildSearchUrl(search);
      if (targetUrl) {
        const url = new URL(targetUrl);
        if (search.type === 'complete') {
          url.searchParams.set('loadComplete', search.id);
        }
        const relativePath = url.href.replace(window.location.origin, '');
        window.open(relativePath, '_blank');
      }
      return;
    }

    const activeFilters = (search.filters || []).map(f => ({
      ...f,
      isEvidence: search.table === 'Evidence',
    }));

    const targetUrl = buildSearchUrl({ ...search, filters: activeFilters });
    if (targetUrl) {
      const relativePath = targetUrl.replace(window.location.origin, '');
      window.open(relativePath, '_blank');
    }
  };

  const handleNavigateWithColumns = search => {
    if (!search.searchContext) return;

    const { view, tab, source } = search.searchContext;

    // If complete search, use saved searchQuery
    if (search.type === 'complete') {
      const url = new URL(`${window.location.origin}/search`);

      // Add base search parameters
      if (search.searchQuery) {
        const searchParams = new URLSearchParams(search.searchQuery);
        searchParams.forEach((value, key) => {
          url.searchParams.set(key, value);
        });
      } else {
        // Fallback to searchContext
        url.searchParams.set('view', view || 'ALL');
        url.searchParams.set('tab', tab || 'GDA');
        url.searchParams.set('source', source || 'ALL');
        url.searchParams.set('idents', 'ALL');
      }

      // Add filters if they exist
      if (search.filters && search.filters.length > 0) {
        const contextParams = ['view', 'tab', 'source', 'idents'];
        const columnFilters = search.filters.filter(
          f =>
            !contextParams.includes(f.name) &&
            f.value !== undefined &&
            f.value !== null &&
            f.value !== '' &&
            !(Array.isArray(f.value) && f.value.length === 0),
        );

        if (columnFilters.length > 0) {
          const encodedFilters = encodeData(columnFilters);
          url.searchParams.set('f', encodedFilters);
        }
      }

      // Store the search data temporarily for loading
      const dataToStore = {
        columnVisibilityModel: search.columnVisibilityModel,
        columnOrder: search.columnOrder,
        name: search.name,
      };
      
      const tempKey = `_temp_loadComplete_${search.id}`;
      localStorage.setItem(tempKey, JSON.stringify(dataToStore));

      // Add parameter to load columns
      url.searchParams.set('loadComplete', search.id);
      
      const relativePath = url.href.replace(window.location.origin, '');
      window.open(relativePath, '_blank');
    } else {
      // Columns only
      const url = new URL(`${window.location.origin}/search`);

      url.searchParams.set('view', view || 'ALL');
      url.searchParams.set('tab', tab || 'GDA');
      url.searchParams.set('source', source || 'ALL');
      url.searchParams.set('idents', 'ALL');
      
      if (search.searchContext && search.searchContext.isEvidence !== undefined) {
        url.searchParams.set('isEvidence', search.searchContext.isEvidence.toString());
      }

      // Store the column data temporarily for loading
      const tempKey = `_temp_loadColumns_${search.id}`;
      
      localStorage.setItem(
        tempKey,
        JSON.stringify({
          columnVisibilityModel: search.columnVisibilityModel || search.filters,
          columnOrder: search.columnOrder,
          name: search.name,
        }),
      );

      url.searchParams.set('loadColumns', search.id);

      const relativePath = url.href.replace(window.location.origin, '');
      window.open(relativePath, '_blank');
    }
  };

  const handleDeleteSearch = async searchId => {
    try {
      const searchToDelete = savedSearches.find(s => s.id === searchId);
      
      if (searchToDelete?.isLocal) {
        localStorage.removeItem(searchId);
      } else {
        await deleteFilter(searchId);
      }

      const updatedSearches = savedSearches.filter(s => s.id !== searchId);
      setSavedSearches(updatedSearches);

      setShowDeleteAlert(true);
      setTimeout(() => setShowDeleteAlert(false), 3000);
    } catch (error) {
      console.error('Error deleting search:', error);
      setError('Failed to delete search');
    }
  };

  const handleClearAllSearches = async () => {
    try {
      const searchesToDelete = filteredSearches;

      const localItems = searchesToDelete.filter(s => s.isLocal);
      const cloudItems = searchesToDelete.filter(s => !s.isLocal);

      localItems.forEach(item => localStorage.removeItem(item.id));

      if (cloudItems.length > 0) {
        await Promise.all(cloudItems.map(search => deleteFilter(search.id)));
      }

      const remainingSearches = savedSearches.filter(s => !searchesToDelete.find(d => d.id === s.id));
      setSavedSearches(remainingSearches);
      setShowDeleteAlert(true);
      setTimeout(() => setShowDeleteAlert(false), 3000);
    } catch (error) {
      console.error('Error clearing all searches:', error);
      setError('Failed to clear all searches');
    }
  };

  const getSearchDetails = filters => {
    if (!Array.isArray(filters) || filters.length === 0) {
      return { filters: 'No column filters applied' };
    }

    const contextParams = ['view', 'tab', 'source', 'idents'];
    const columnFilters = filters.filter(f => !contextParams.includes(f.name));

    if (columnFilters.length === 0) {
      return { filters: 'No column filters applied' };
    }

    if (columnFilters.length === 0) {
      return { filters: 'No column filters applied' };
    }

    const filtersStr = columnFilters
      .filter(f => f.value && f.value !== '' && !(Array.isArray(f.value) && f.value.length === 0))
      .map(f => {
        const label = f.label || f.name || '';
        const value = Array.isArray(f.value) ? f.value.join(', ') : f.value;
        const operator = f.operator ? ` ${operatorMap[f.operator] || f.operator} ` : ': ';
        return `${label}${operator}${value}`;
      })
      .join(', ');

    return { filters: filtersStr || 'No column filters applied' };
  };

  const formatDate = timestamp => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isAuthenticated) {
    return (
      <div className={favoritesClass} id="favorites">
        <h2>Saved Searches</h2>
        <div className="wrap-input100" />
        <div style={{ padding: '20px 0' }}>
          <p className="label-input100" style={{ color: '#999' }}>
            Please log in to see your saved searches.
          </p>
        </div>
      </div>
    );
  }

  const handleSaveEdit = async filterId => {
    if (!editName.trim()) return;

    try {
      await updateFilter(filterId, { name: editName });

      setSavedSearches(prev => prev.map(s => (s.id === filterId ? { ...s, name: editName } : s)));

      setEditId(null);
      setEditName('');
      setShowUpdateAlert(true);
      setTimeout(() => setShowUpdateAlert(false), 3000);
    } catch (error) {
      console.error('Error updating filter name:', error);
      setError('Failed to update filter name');
    }
  };

  return (
    <>
      {showDeleteAlert && <Alert message="Search deleted successfully" type="success" />}
      {showUpdateAlert && <Alert message="Filter saved successfully!" type="success" />}
      {error && <Alert message={error} type="error" />}

      <div className={favoritesClass} id="favorites">
        <h2>Saved Searches</h2>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '20px',
            borderBottom: '2px solid #e5e7eb',
          }}
        >
          <button
            onClick={() => setActiveTab('filters')}
            style={{
              padding: '10px 20px',
              background: activeTab === 'filters' ? '#0a104f' : 'transparent',
              color: activeTab === 'filters' ? 'white' : '#6b7280',
              border: 'none',
              borderBottom: activeTab === 'filters' ? '2px solid #0a104f' : '2px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'filters' ? '600' : '400',
              fontSize: '14px',
              transition: 'all 0.2s',
              marginBottom: '-2px',
            }}
          >
            Saved filters
          </button>
          <button
            onClick={() => setActiveTab('searches')}
            style={{
              padding: '10px 20px',
              background: activeTab === 'searches' ? '#0a104f' : 'transparent',
              color: activeTab === 'searches' ? 'white' : '#6b7280',
              border: 'none',
              borderBottom: activeTab === 'searches' ? '2px solid #0a104f' : '2px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'searches' ? '600' : '400',
              fontSize: '14px',
              transition: 'all 0.2s',
              marginBottom: '-2px',
            }}
          >
            Saved searches
          </button>
          <button
            onClick={() => setActiveTab('columns')}
            style={{
              padding: '10px 20px',
              background: activeTab === 'columns' ? '#0a104f' : 'transparent',
              color: activeTab === 'columns' ? 'white' : '#6b7280',
              border: 'none',
              borderBottom: activeTab === 'columns' ? '2px solid #0a104f' : '2px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'columns' ? '600' : '400',
              fontSize: '14px',
              transition: 'all 0.2s',
              marginBottom: '-2px',
            }}
          >
            Saved columns
          </button>
        </div>

        <div className="wrap-input100" />

        {isLoading ? (
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden"></span>
            </div>
            <p style={{ marginTop: '16px', color: '#666' }}>Loading your saved searches...</p>
          </div>
        ) : filteredSearches.length === 0 ? (
          <div style={{ padding: '20px 0' }}>
            <p className="label-input100" style={{ color: '#999' }}>
              {activeTab === 'filters' && 'You don`t have any saved filters.'}
              {activeTab === 'searches' && 'You don`t have any saved searches.'}
              {activeTab === 'columns' && 'You don`t have any saved columns selection.'}
            </p>
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <p className="input100">
                {activeTab === 'filters' && `Saved filters: ${filteredSearches.length}`}
                {activeTab === 'searches' && `Saved searches: ${filteredSearches.length}`}
                {activeTab === 'columns' && `Saved columns: ${filteredSearches.length}`}
              </p>
              {filteredSearches.length > 0 && (
                <SharedButton
                  id="clearAll"
                  size="xs"
                  onClick={handleClearAllSearches}
                  type="button"
                  className="BtnAccion mx-0 mb-2"
                  style={{ height: '30px', width: '10%', fontSize: '12px' }}
                >
                  Clear All
                </SharedButton>
              )}
            </div>

            <div style={{ marginTop: '20px' }}>
              {filteredSearches.map(search => (
                <div key={search.id} className="saved-filter-card mb-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: '8px' }}>
                        {editId === search.id ? (
                          <input
                            type="text"
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleSaveEdit(search.id);
                            }}
                            style={{
                              fontSize: '16px',
                              padding: '2px 6px',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              width: '70%',
                            }}
                            autoFocus
                          />
                        ) : (
                          <span className="text-pink font-bold" style={{ fontSize: '16px' }}>
                            {search.name}
                          </span>
                        )}

                        <span style={{ marginLeft: '12px', fontSize: '12px', color: '#666' }}>
                          {formatDate(search.savedAt)}
                        </span>
                      </div>
                      <div style={{ marginBottom: '4px' }}>
                        <span
                          style={{
                            background: search.table === 'Evidence' ? '#dbeafe' : '#fce7f3',
                            color: search.table === 'Evidence' ? '#1e40af' : '#9f1239',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500',
                          }}
                        >
                          {search.table}
                        </span>
                      </div>
                      <div style={{ marginTop: '8px' }}>
                        {activeTab === 'filters' &&
                          (() => {
                            const d = getSearchDetails(search.filters);
                            return (
                              <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
                                <strong>Column filters:</strong> {d.filters}
                              </div>
                            );
                          })()}
                        {activeTab === 'searches' && search.type === 'complete' && (
                          <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
                            <div
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                background: '#e3f2fd',
                                color: '#1976d2',
                                padding: '4px 10px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600',
                                marginBottom: '6px',
                              }}
                            >
                              <RectangleStackIcon className="w-4 h-4" />
                              COMPLETE SEARCH
                            </div>
                            <br />
                            <strong>Includes:</strong> Filters + Columns + Order
                            {search.searchContext && (
                              <>
                                <br />
                                <strong>Context:</strong> {search.searchContext.view} - {search.searchContext.tab}
                              </>
                            )}
                          </div>
                        )}
                        {activeTab === 'searches' && search.type !== 'complete' && (
                          <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
                            <strong>Search:</strong> {search.searchQuery || 'N/A'}
                          </div>
                        )}
                        {activeTab === 'columns' && search.type === 'complete' && (
                          <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
                            <div
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                background: '#e3f2fd',
                                color: '#1976d2',
                                padding: '4px 10px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600',
                                marginBottom: '6px',
                              }}
                            >
                              <RectangleStackIcon className="w-4 h-4" />
                              COMPLETE SEARCH
                            </div>
                            <br />
                            <strong>Includes:</strong> Filters + Columns + Order
                            {search.searchContext && (
                              <>
                                <br />
                                <strong>Context:</strong> {search.searchContext.view} -{' '}
                                {search.searchContext.tab}
                              </>
                            )}
                          </div>
                        )}
                        {activeTab === 'columns' && search.type === 'columns' && (
                          <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
                            <strong>Visible columns:</strong>{' '}
                            {search.columnVisibilityModel && typeof search.columnVisibilityModel === 'object'
                              ? Object.values(search.columnVisibilityModel).filter(v => v === true).length
                              : 0}{' '}
                            columns
                            {search.columnOrder && (
                              <>
                                <br />
                                <strong>Custom order:</strong> Yes ({search.columnOrder.length} columns)
                              </>
                            )}
                            {search.searchContext && (
                              <>
                                <br />
                                <strong>Context:</strong> {search.searchContext.view} - {search.searchContext.tab}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                      {editId === search.id ? (
                        <>
                          <SharedButton
                            size="xs"
                            type="button"
                            onClick={() => handleSaveEdit(search.id)}
                            disabled={!editName.trim()}
                          >
                            Save
                          </SharedButton>
                          <SharedButton
                            size="xs"
                            type="button"
                            variant="secondary"
                            onClick={() => {
                              setEditId(null);
                              setEditName('');
                            }}
                          >
                            Cancel
                          </SharedButton>
                        </>
                      ) : (
                        <>
                          <SharedButton
                            size="xs"
                            type="button"
                            onClick={() => {
                              setEditId(search.id);
                              setEditName(search.name);
                            }}
                          >
                            Edit
                          </SharedButton>
                          {activeTab === 'columns' ? (
                            <SharedButton
                              size="xs"
                              type="button"
                              onClick={() => handleNavigateWithColumns(search)}
                            >
                              <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                              {search.type === 'complete' ? 'Apply All' : 'Apply Columns'}
                            </SharedButton>
                          ) : (
                            <SharedButton
                              size="xs"
                              type="button"
                              onClick={() => handleNavigateToSearch(search)}
                            >
                              <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                              Open
                            </SharedButton>
                          )}
                          <SharedButton size="xs" type="button" onClick={() => handleDeleteSearch(search.id)}>
                            <TrashIcon className="h-4 w-4" />
                          </SharedButton>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ProfileFavorites;
